import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import axiosInstance from '../../../utils/axios';
import Popover from '../../../components/gps/Popover';
import { Num } from '../../../components/gps';

/* ============================================================================
   Notificacoes - visual novo.

   Substitui IconButton/Badge/Popover/List/Chip do MUI. A logica ficou intacta,
   porque cada pedaco dela existe por um motivo ja documentado:

     - contador recarrega a cada 60s (`notificacoes/contador`);
     - a lista so e buscada ao ABRIR o painel, nao a cada minuto;
     - clicar marca como lida e navega; o mapa DESTINO_POR_TIPO resolve o
       historico inteiro de notificacoes gravadas SEM `link`, que antes nao levavam
       a lugar nenhum.

   O QUE MUDA:

   1. O contador nao mostra mais numero dentro de bolinha vermelha sempre: quando
      e ZERO, a bolinha desaparece. `Badge` com `badgeContent={0}` do MUI ainda
      pintava o circulo, e um "0" vermelho no canto da tela parece alerta.

   2. Cada item mostra o tom do tipo (PROBLEMA vermelho, SUPORTE alerta, DUVIDA
      info, SUGESTAO ok) - a mesma escala que o resto do sistema, em vez das cores
      do MUI.

   3. O tempo relativo ganhou `title` com a data completa. "3 d" e bom para
      triagem e ruim para investigar.
   ============================================================================ */

type Notificacao = {
  codigo: number;
  tipo: string;
  titulo: string | null;
  descricao: string | null;
  lida: boolean;
  link: string | null;
  createdAt: string;
  tipoMensagem?: string | null;
  remetenteNome?: string | null;
};

function tempoRelativo(data: string): string {
  const diff = Date.now() - new Date(data).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h`;
  const d = Math.floor(h / 24);
  return `${d} d`;
}

function tomTipo(tipo?: string | null) {
  const t = String(tipo || '').toUpperCase();
  if (t === 'PROBLEMA') return 'bg-danger-soft text-danger';
  if (t === 'SUPORTE') return 'bg-warn-soft text-warn';
  if (t === 'DÚVIDA' || t === 'DUVIDA') return 'bg-info-soft text-info';
  if (t === 'SUGESTÃO' || t === 'SUGESTAO') return 'bg-ok-soft text-ok';
  return 'bg-soft text-muted-foreground';
}

export default function NotificacoesPopover() {
  const { push } = useRouter();
  const [total, setTotal] = useState(0);
  const [itens, setItens] = useState<Notificacao[]>([]);
  const [carregando, setCarregando] = useState(false);
  const timer = useRef<ReturnType<typeof setInterval> | undefined>(undefined);

  const carregarContador = useCallback(async () => {
    try {
      const res = await axiosInstance.get<{ total: number }>('notificacoes/contador');
      setTotal(res.data?.total || 0);
    } catch (e) {
      /* silencioso: contador acessorio nao pode virar erro na faixa superior */
    }
  }, []);

  const carregarLista = useCallback(async () => {
    setCarregando(true);
    try {
      const res = await axiosInstance.get<Notificacao[]>('notificacoes');
      setItens(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setItens([]);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregarContador();
    timer.current = setInterval(carregarContador, 60000);
    return () => {
      if (timer.current) clearInterval(timer.current);
    };
  }, [carregarContador]);

  const aoClicar = async (n: Notificacao, fechar: () => void) => {
    try {
      if (!n.lida) {
        await axiosInstance.post(`notificacoes/${n.codigo}/lida`, {});
        setItens((prev) => prev.map((i) => (i.codigo === n.codigo ? { ...i, lida: true } : i)));
        setTotal((t) => Math.max(0, t - 1));
      }
    } catch (e) {
      /* segue: nao poder marcar como lida nao deve impedir a navegacao */
    }
    fechar();

    // Fallback por TIPO. As notificacoes ja gravadas no banco vieram sem `link`
    // (a API so preenchia nas de regional), entao clicar nelas nao fazia nada -
    // era o "clique na notificacao nao vai". Corrigir apenas a API resolveria
    // dali para frente e deixaria todo o historico morto.
    const DESTINO_POR_TIPO: Record<string, string> = {
      MENSAGEM: '/dashboard/tickets',
      TICKET: '/dashboard/tickets',
      ACESSO_REGIONAL: '/dashboard/regionais/acessos',
    };

    const destino = n.link || DESTINO_POR_TIPO[String(n.tipo || '').toUpperCase()];
    if (destino) push(destino);
  };

  const marcarTodas = async () => {
    try {
      await axiosInstance.post('notificacoes/marcar-todas-lidas', {});
      setItens((prev) => prev.map((i) => ({ ...i, lida: true })));
      setTotal(0);
    } catch (e) {
      /* segue */
    }
  };

  return (
    <Popover
      rotuloGatilho="Notificações"
      largura="w-[380px]"
      aoAbrir={carregarLista}
      // Sem `text-muted-foreground` no <span> abaixo: o icone vive na faixa
      // superior, que agora tem a cor da paleta - cinza escuro ali fica quase
      // invisivel. Herdando a cor do cabecalho, ele acompanha qualquer preset.
      //
      // (O comentario fica AQUI, e nao dentro do `gatilho={...}`: `{/* */}` como
      // primeiro item de um container de expressao JSX nao e comentario, e um
      // objeto vazio - e o build morre com "Expected `}`". Ja tropecei nisto antes.)
      gatilho={
        <span className="relative grid h-8 w-8 place-items-center text-[14px]">
          <span aria-hidden>&#9873;</span>
          {/* Zero nao pinta bolinha: um "0" vermelho no canto parece alerta. */}
          {total > 0 && (
            <span className="absolute -right-0.5 -top-0.5 grid h-[16px] min-w-[16px] place-items-center rounded-full bg-danger px-1 text-[10px] font-bold text-white">
              {total > 99 ? '99+' : total}
            </span>
          )}
        </span>
      }
    >
      {(fechar) => (
        <>
          <div className="flex items-center justify-between border-b border-line px-3 py-2">
            <span className="text-[12.5px] font-bold">Notificações</span>
            {total > 0 && (
              <button
                type="button"
                onClick={marcarTodas}
                className="text-[11px] font-semibold text-primary hover:underline"
              >
                Marcar todas lidas
              </button>
            )}
          </div>

          <div className="max-h-[420px] overflow-y-auto">
            {carregando && itens.length === 0 && (
              <div className="py-8 text-center text-[12px] text-faint">Carregando...</div>
            )}
            {!carregando && itens.length === 0 && (
              <div className="py-8 text-center text-[12px] text-faint">Nenhuma notificação.</div>
            )}

            {itens.map((n) => (
              <button
                key={n.codigo}
                type="button"
                onClick={() => aoClicar(n, fechar)}
                className={`flex w-full gap-2 border-b border-line px-3 py-2.5 text-left last:border-0 hover:bg-soft ${
                  n.lida ? '' : 'bg-primary/5'
                }`}
              >
                <span
                  className={`mt-1.5 h-2 w-2 flex-none rounded-full ${n.lida ? 'bg-transparent' : 'bg-danger'}`}
                  aria-hidden
                />
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-1.5">
                    <span className="text-[12px] font-bold">{n.titulo || 'Notificação'}</span>
                    {(n.tipoMensagem || (n.tipo && n.tipo !== 'MENSAGEM')) && (
                      <span
                        className={`inline-flex h-[17px] items-center rounded px-1.5 text-[10px] font-bold ${tomTipo(
                          n.tipoMensagem || n.tipo
                        )}`}
                      >
                        {n.tipoMensagem || n.tipo}
                      </span>
                    )}
                  </span>
                  {n.descricao && (
                    <span className="mt-0.5 block truncate text-[12px] text-muted-foreground">{n.descricao}</span>
                  )}
                  <span
                    className="mt-0.5 block text-[11px] text-faint"
                    title={new Date(n.createdAt).toLocaleString('pt-BR')}
                  >
                    <Num>{tempoRelativo(n.createdAt)}</Num>
                    {n.remetenteNome ? ` · ${n.remetenteNome}` : ''}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </>
      )}
    </Popover>
  );
}
